import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, MessageCircle, Edit, Plus, MapPin, Mail, Phone, Calendar, CheckSquare, Trash2, Send,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmtDate, fmtDateTime, statusColor, msgStatusColor } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ClientFormDialog from "@/components/ClientFormDialog";

const EVENT_LABEL = { created: "Cliente cadastrado", updated: "Cadastro atualizado", message: "Mensagem enviada", tag: "Tag alterada", followup: "Follow-up criado" };

export default function ClientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [fuOpen, setFuOpen] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [fu, setFu] = useState({ due_date: "", note: "" });

  const load = () => api.get(`/clients/${id}`).then((r) => setClient(r.data));
  useEffect(() => { load(); api.get("/tags").then((r) => setAllTags(r.data)); }, [id]);

  if (!client) return <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" /></div>;

  const addTag = async (tag) => {
    if (!tag) return;
    await api.post(`/clients/${id}/tags`, { tag });
    toast.success("Tag adicionada!");
    setTagOpen(false); setNewTag(""); load();
    api.get("/tags").then((r) => setAllTags(r.data));
  };

  const saveFu = async () => {
    if (!fu.due_date) return toast.error("Informe a data.");
    try {
      await api.post("/followups", { client_id: id, due_date: fu.due_date, note: fu.note });
      toast.success("Follow-up criado!");
      setFuOpen(false); setFu({ due_date: "", note: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openWhatsApp = () => {
    if (!client.whatsapp) return toast.error("Cliente sem WhatsApp.");
    window.open(`https://wa.me/${client.whatsapp.replace(/\D/g, "")}`, "_blank");
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="client-profile-page">
      <button onClick={() => navigate("/clientes")} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Voltar para clientes
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: info */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm text-center">
            <div className="h-20 w-20 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl font-bold mx-auto">
              {client.name?.[0]}
            </div>
            <h1 className="font-display text-xl font-bold text-slate-900 mt-4">{client.name}</h1>
            <span className={`inline-block mt-2 text-xs px-2.5 py-1 rounded-full border ${statusColor(client.status)}`}>{client.status}</span>
            <div className="flex flex-wrap gap-1.5 justify-center mt-3">
              {(client.tags || []).map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">{t}</span>)}
            </div>
            <div className="grid grid-cols-1 gap-2 mt-6">
              <Button onClick={openWhatsApp} className="bg-emerald-500 hover:bg-emerald-600 w-full" data-testid="profile-whatsapp-button">
                <MessageCircle className="h-4 w-4 mr-2" /> Enviar WhatsApp
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => setEditOpen(true)} variant="outline" data-testid="profile-edit-button"><Edit className="h-4 w-4 mr-1" /> Editar</Button>
                <Button onClick={() => setTagOpen(true)} variant="outline" data-testid="profile-tag-button"><Plus className="h-4 w-4 mr-1" /> Tag</Button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-3 text-sm">
            <h3 className="font-semibold text-slate-800 mb-2">Informações</h3>
            <InfoRow icon={MessageCircle} label="WhatsApp" value={client.whatsapp} mono />
            <InfoRow icon={Phone} label="Telefone" value={client.phone} />
            <InfoRow icon={Mail} label="E-mail" value={client.email} />
            <InfoRow icon={Calendar} label="Nascimento" value={fmtDate(client.birth_date)} />
            <InfoRow icon={MapPin} label="Endereço" value={[client.address, client.city].filter(Boolean).join(", ")} />
            {client.notes && <div className="pt-2 border-t border-slate-100"><p className="text-xs text-slate-400 mb-1">Observações</p><p className="text-slate-600">{client.notes}</p></div>}
          </div>
        </div>

        {/* Right: timeline + messages + followups */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Linha do tempo</h3>
              <Button size="sm" variant="outline" onClick={() => setFuOpen(true)} data-testid="profile-add-followup"><CheckSquare className="h-4 w-4 mr-1" /> Follow-up</Button>
            </div>
            <div className="relative pl-6" data-testid="client-timeline">
              <div className="absolute left-2 top-1 bottom-1 w-px bg-slate-200" />
              {client.events.length === 0 && <p className="text-sm text-slate-400">Sem eventos.</p>}
              {client.events.map((ev) => (
                <div key={ev.id} className="relative pb-5 last:pb-0">
                  <div className="absolute -left-[18px] top-1 h-3 w-3 rounded-full bg-emerald-500 ring-4 ring-emerald-100" />
                  <p className="text-xs text-slate-400">{fmtDateTime(ev.created_at)}</p>
                  <p className="text-sm text-slate-700 font-medium">{ev.description || EVENT_LABEL[ev.kind] || ev.kind}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
            <h3 className="font-semibold text-slate-800 mb-4">Histórico de comunicação</h3>
            <div className="space-y-2">
              {client.messages.length === 0 && <p className="text-sm text-slate-400">Nenhuma mensagem enviada ainda.</p>}
              {client.messages.map((m) => (
                <div key={m.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <Send className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-medium text-slate-700">{m.campaign_name || "Mensagem"}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${msgStatusColor(m.status)}`}>{m.status}</span>
                      <span className="text-[11px] text-slate-400 ml-auto">{fmtDateTime(m.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">{m.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {client.followups.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
              <h3 className="font-semibold text-slate-800 mb-4">Follow-ups</h3>
              <div className="space-y-2">
                {client.followups.map((f) => (
                  <div key={f.id} className={`flex items-center gap-3 p-3 rounded-lg border ${f.done ? "bg-slate-50 border-slate-100 opacity-60" : "bg-amber-50 border-amber-100"}`}>
                    <CheckSquare className="h-4 w-4 text-amber-600 shrink-0" />
                    <div className="flex-1"><p className="text-sm font-medium text-slate-700">{fmtDate(f.due_date)}</p><p className="text-xs text-slate-500">{f.note}</p></div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <ClientFormDialog open={editOpen} onOpenChange={setEditOpen} client={client} onSaved={load} />

      {/* Tag dialog */}
      <Dialog open={tagOpen} onOpenChange={setTagOpen}>
        <DialogContent data-testid="add-tag-dialog">
          <DialogHeader><DialogTitle>Adicionar Tag</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-2 py-2">
            {allTags.map((t) => <button key={t.id} onClick={() => addTag(t.name)} className="text-xs px-3 py-1.5 rounded-full border bg-white text-slate-600 border-slate-200 hover:border-emerald-400 hover:text-emerald-700">{t.name}</button>)}
          </div>
        </DialogContent>
      </Dialog>

      {/* Followup dialog */}
      <Dialog open={fuOpen} onOpenChange={setFuOpen}>
        <DialogContent data-testid="followup-dialog">
          <DialogHeader><DialogTitle>Novo Follow-up</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Data</Label><Input type="date" value={fu.due_date} onChange={(e) => setFu({ ...fu, due_date: e.target.value })} className="mt-1.5" data-testid="followup-date-input" /></div>
            <div><Label>Observação</Label><Textarea value={fu.note} onChange={(e) => setFu({ ...fu, note: e.target.value })} className="mt-1.5" placeholder="Ex: Verificar se cliente deseja agendar retorno." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFuOpen(false)}>Cancelar</Button>
            <Button onClick={saveFu} className="bg-emerald-500 hover:bg-emerald-600" data-testid="followup-save-button">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
      <span className="text-slate-400 text-xs w-20">{label}</span>
      <span className={`text-slate-700 flex-1 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}
